import { useCallback, useEffect, useMemo, useState } from "react";
import { History, Loader2, RefreshCw } from "lucide-react";
import { api, formatDateTime } from "@/api";

// Settings → Recent Activity (owner, 2026-10-11: below the users, on this site and the Xero Setup site - the same
// component on both). The newest entries of the activity log (kept a year): sign-ins, failures, invitations,
// password / account changes and Xero. Passwords and ID numbers are never logged.
const EVENT_LABELS = {
  SIGNED_IN: "Signed in",
  SIGNED_OUT: "Signed out",
  SIGN_IN_FAILED: "Wrong email or password",
  SIGN_IN_REFUSED: "Sign-in refused",
  MFA_FAILED: "Wrong MFA code",
  MFA_SET_UP: "Set up MFA",
  MFA_RESET: "MFA reset",
  PASSWORD_SET: "Chose own password",
  USER_INVITED: "Invited",
  INVITATION_RESENT: "Invitation resent",
  PASSWORD_RESET: "Password reset",
  USER_UNLOCKED: "Unlocked",
  USER_UPDATED: "Account / MFA changed",
  USER_EDITED: "Details edited",
  USER_DELETED: "User deleted",
  XERO_CONNECTED: "Xero connected",
  XERO_DISCONNECTED: "Xero disconnected",
  XERO_CLIENT_BLOCKED: "Organisation blocked",
  XERO_CLIENT_UNBLOCKED: "Organisation unblocked",
  XERO_CLIENT_DISCONNECTED: "Client's Xero disconnected",
  AFFIDAVIT_SENT: "Affidavit sent",
};
const BAD = /FAILED|REFUSED/;
const FIELD_NAMES = { first_name: "name", last_name: "surname", email: "email" };

function describe(row) {
  const d = row.detail || {};
  const parts = [];
  if (d.reason) parts.push(d.reason);
  if (d.locked) parts.push("account locked for 15 minutes");
  if (d.invitation_sent === true) parts.push("invitation emailed");
  if (d.invitation_sent === false) parts.push("invitation email NOT sent");
  if (d.active !== undefined) parts.push(d.active ? "account enabled" : "account disabled");
  if (d.mfa_enabled !== undefined) parts.push(d.mfa_enabled ? "MFA on (set up again)" : "MFA off");
  Object.entries(FIELD_NAMES).forEach(([key, label]) => {
    if (d[key]?.from !== undefined) parts.push(`${label}: ${d[key].from} → ${d[key].to}`);
  });
  if (d.name) parts.push(d.name);
  if (d.organisation) parts.push(d.organisation);
  if (row.event === "AFFIDAVIT_SENT") {
    parts.push([d.company, d.affidavit, d.level, d.commissioner && `commissioner ${d.commissioner}`].filter(Boolean).join(", "));
    if (d.invoice) parts.push(`invoice: ${d.invoice}`);
  }
  if (row.by && row.by !== row.email) parts.push(`by ${row.by}`);
  if (row.ip) parts.push(`IP ${row.ip}`);
  return parts.filter(Boolean).join(" · ");
}

export default function ActivityPanel() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      setRows(await api("/admin/activity"));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const words = filter.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (rows || []).map((r) => ({ ...r, what: EVENT_LABELS[r.event] || r.event, text: describe(r) }))
      .filter((r) => words.every((w) => `${r.email} ${r.what} ${r.text}`.toLowerCase().includes(w)));
  }, [rows, filter]);

  return (
    <section className="bee-settings-panel bee-activity-panel" aria-labelledby="activity-title" data-testid="activity-panel">
      <div className="bee-settings-head">
        <h2 id="activity-title"><History size={20} /> Recent Activity</h2>
        <button type="button" className="bee-btn-small" onClick={load} disabled={busy} data-testid="button-activity-refresh">
          {busy ? <Loader2 size={14} className="bee-spin" /> : <RefreshCw size={14} />} <span>Refresh</span>
        </button>
      </div>
      <p className="bee-settings-intro">
        The newest {rows ? rows.length : ""} entries of the activity log (kept for a year), newest first. Failed sign-ins
        are shown in red.
      </p>
      <input
        className="bee-input bee-activity-filter"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter, e.g. an email address or Signed in"
        aria-label="Filter the activity"
        data-testid="activity-filter"
      />
      {error && <div className="bee-auth-error" role="alert">{error}</div>}
      {!rows ? (
        !error && <div className="bee-settings-loading"><Loader2 size={22} className="bee-spin" /></div>
      ) : shown.length === 0 ? (
        <p className="bee-blocked-empty">{rows.length ? "Nothing matches the filter." : "No activity yet."}</p>
      ) : (
        <div className="bee-activity-wrap">
          <table className="bee-activity" data-testid="activity-table">
            <thead>
              <tr><th>When</th><th>User</th><th>What</th><th>Details</th></tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={`${r.at}-${i}`} className={BAD.test(r.event) ? "is-bad" : undefined}>
                  <td className="bee-activity-when">{formatDateTime(r.at)}</td>
                  <td className="bee-activity-user">{r.email}</td>
                  <td className="bee-activity-what">{r.what}</td>
                  <td className="bee-activity-detail">{r.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
